import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Section = styled.section`
  padding: 80px 0;
  background-color: #f8fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  color: #1f2937;

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.25rem;
  color: #6b7280;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const MeetingHeader = styled(motion.div)`
  background: linear-gradient(135deg, #1e40af, #3b82f6);
  color: white;
  padding: 2rem;
  border-radius: 16px;
  margin-bottom: 3rem;
  text-align: center;
`;

const MeetingTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const MeetingDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  text-align: center;
  
  .label {
    font-size: 0.875rem;
    opacity: 0.8;
    margin-bottom: 0.25rem;
  }
  
  .value {
    font-size: 1rem;
    font-weight: 500;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  border: none;
  background: none;
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.active ? '#1e40af' : '#6b7280'};
  border-bottom: 2px solid ${props => props.active ? '#1e40af' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: #1e40af;
  }
`;

const TabContent = styled(motion.div)`
  min-height: 400px;
`;

const ActionItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
`;

const ActionItemCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  border: 1px solid #e5e7eb;
  border-left: 4px solid #1e40af;
`;

const ActionItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const Assignee = styled.div`
  font-weight: 600;
  color: #1e40af;
  font-size: 1rem;
`;

const PriorityBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch(props.priority) {
      case 'high': return '#fef2f2';
      case 'medium': return '#fef3c7';
      case 'low': return '#d1fae5';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#059669';
      default: return '#6b7280';
    }
  }};
`;

const ActionDescription = styled.p`
  color: #374151;
  line-height: 1.6;
  margin: 0;
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const OverviewCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  border: 1px solid #e5e7eb;
`;

const CardTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    color: #1e40af;
  }
`;

const CardContent = styled.div`
  color: #6b7280;
  line-height: 1.6;

  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: #374151;
  }
`;

const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const Metric = styled.div`
  text-align: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;

  .value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e40af;
    display: block;
  }

  .label {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }
`;

const MeetingNotes = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [activeTab, setActiveTab] = useState('overview');

  const actionItems = [
    {
      assignee: 'TaRita Johnson',
      priority: 'high',
      description: 'Coordinate with Andrea and Brandon on diversity committee formation. Meet with Andrea and Brandon to get approval and coordinate the formation of the diversity-focused Tech Week committee.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'medium',
      description: 'Explore bus wrap sponsorship for Tech Week promotion. Investigate the cost and logistics of wrapping a Grand Rapids bus with Tech Week branding for 1-2 months, and potentially routing it to Tech Week events during the week as both promotion and transportation.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'medium',
      description: 'Explore partnership with Mutually Human for registration solution. Follow up with Mutually Human as they mentioned having a complete solution for event registration that could help simplify the check-in process for Tech Week.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'high',
      description: 'Reserve kickoff event tickets for student groups. Allocate reserved tickets for the Tech Week kickoff event for targeted student organizations at colleges, universities, and high schools including groups like Developers Den, Black Engineers, HBCU/HSI groups, and women\'s groups at Calvin and Grand Valley.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'medium',
      description: 'Add partnership interest form to Tech Week website. Create and implement a form on the Tech Week website for people interested in partnerships, speaking opportunities, hosting venues, or sponsorships, allowing the engagement committee to match speakers with venues and connect interested parties year-round.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'medium',
      description: 'Follow up with Muskegon Innovation Hub for Tech Week participation. Reconnect with Megan at the Muskegon Innovation Hub who expressed interest in hosting Tech Week events but was approached too late this year, to ensure their participation and involvement in planning for next year.'
    },
    {
      assignee: 'Chip LaFleur',
      priority: 'low',
      description: 'Sync Notion library to GitHub. Figure out how to sync up the Notion library to GitHub to enable the AI workflow that currently works with Obsidian but needs to function with the more collaborative Notion platform.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'high',
      description: 'Plan February or March Tech Week information event. Organize a public event (potentially happy hour format) for February or March 2026 at The Right Place or another community location for people interested in learning about or getting involved with Tech Week, serving as a pipeline entry point six months before the event.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'high',
      description: 'Implement QR code evaluation system for Tech Week events. Create a QR code-based evaluation system that can be placed at each Tech Week event venue to collect attendee feedback on event quality and provide speakers with performance feedback they currently don\'t receive.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'medium',
      description: 'Establish Tech Week hub venue for next year. Secure a central hub location for Tech Week where attendees can gather, see poster boards of events, sit and relax, and have a meeting place throughout the week. Consider Bamboo which has already offered to be a hub.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'medium',
      description: 'Organize private pre-reception for student attendees at kickoff. Create a special private reception one hour before the main Tech Week kickoff event specifically for student attendees to provide exposure and networking opportunities before the adult VIP reception.'
    },
    {
      assignee: 'TaRita Johnson',
      priority: 'high',
      description: 'Create diversity-focused Tech Week committee. Form and lead a diversity-specific committee for Tech Week that brings together the Hispanic Chamber, Asian Association, Urban League, NAACP, GRCC, and other community organizations to build out diversity-focused events and increase representation.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'high',
      description: 'Outsource registration for the main Tech Week event. Arrange for outsourced registration services for the main Tech Week event next year, implementing on-the-spot name tag printing to streamline the check-in process and eliminate the need to flip through pages of pre-printed badges.'
    },
    {
      assignee: 'Brandon Hamilton',
      priority: 'medium',
      description: 'Partner with Urban League for August pitch event during Tech Week. Reach out to Urban League about shifting their annual pitch event (currently held around August 27th where they give away $100,000+ to multiple businesses) to align with Tech Week timing, and explore opportunities to match their funding contribution with another community partner.'
    }
  ];

  const overviewData = [
    {
      title: 'Attendance & Events',
      icon: 'fas fa-chart-line',
      content: 'Tech Week 2025 drew 15,000+ attendees across 120+ events. The Right Place reported over 16,000 attendees in 2024 across 70+ events.',
      metrics: [
        { value: '120+', label: 'Events (2025)' },
        { value: '15K+', label: 'Attendees (2025)' },
        { value: '16K+', label: 'Attendees (2024)' }
      ]
    },
    {
      title: 'Diversity & Inclusion Focus',
      icon: 'fas fa-users',
      content: 'Major gap identified: Tech Week remains "the best conference no one\'s heard of" with limited public awareness and minimal presence in Black, Brown, and diverse communities. New diversity committee proposed.',
      metrics: [
        { value: 'New', label: 'Diversity Committee' },
        { value: '6+', label: 'Community Partners' },
        { value: '$100K+', label: 'Urban League Funding' }
      ]
    },
    {
      title: 'Registration Improvements',
      icon: 'fas fa-clipboard-check',
      content: 'Committee agreed to outsource registration for next year with on-demand name tag printing to fix check-in bottlenecks. Current system requires flipping through pages of pre-printed badges.',
      metrics: [
        { value: 'New', label: 'Registration System' },
        { value: 'On-demand', label: 'Name Tags' },
        { value: 'Streamlined', label: 'Check-in' }
      ]
    },
    {
      title: 'Year-Round Engagement',
      icon: 'fas fa-calendar-alt',
      content: 'Brandon proposed year-round partnership matchmaking through the website and a pre-event community gathering in February or March to recruit new hosts and speakers.',
      metrics: [
        { value: 'Feb/Mar', label: 'Info Event' },
        { value: 'Year-round', label: 'Partnerships' },
        { value: '6 months', label: 'Early Pipeline' }
      ]
    }
  ];

  return (
    <Section id="meeting-notes">
      <Container>
        <SectionTitle
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          October Committee Meeting
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Key insights and action items from our latest engagement committee meeting
        </SectionSubtitle>

        <MeetingHeader
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <MeetingTitle>Tech Week Engagement Committee Meeting - October</MeetingTitle>
          <MeetingDetails>
            <DetailItem>
              <div className="label">Date</div>
              <div className="value">Tuesday, October 14, 2025</div>
            </DetailItem>
            <DetailItem>
              <div className="label">Duration</div>
              <div className="value">54:28</div>
            </DetailItem>
            <DetailItem>
              <div className="label">Attendees</div>
              <div className="value">20+ Committee Members</div>
            </DetailItem>
          </MeetingDetails>
        </MeetingHeader>

        <TabsContainer>
          <Tab 
            active={activeTab === 'overview'} 
            onClick={() => setActiveTab('overview')}
          >
            Meeting Overview
          </Tab>
          <Tab 
            active={activeTab === 'actions'} 
            onClick={() => setActiveTab('actions')}
          >
            Action Items ({actionItems.length})
          </Tab>
        </TabsContainer>

        <TabContent>
          {activeTab === 'overview' && (
            <OverviewGrid>
              {overviewData.map((item, index) => (
                <OverviewCard
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <CardTitle>
                    <i className={item.icon}></i>
                    {item.title}
                  </CardTitle>
                  <CardContent>
                    {item.content}
                    <MetricsRow>
                      {item.metrics.map((metric, metricIndex) => (
                        <Metric key={metricIndex}>
                          <span className="value">{metric.value}</span>
                          <span className="label">{metric.label}</span>
                        </Metric>
                      ))}
                    </MetricsRow>
                  </CardContent>
                </OverviewCard>
              ))}
            </OverviewGrid>
          )}

          {activeTab === 'actions' && (
            <ActionItemsGrid>
              {actionItems.map((item, index) => (
                <ActionItemCard
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <ActionItemHeader>
                    <Assignee>{item.assignee}</Assignee>
                    <PriorityBadge priority={item.priority}>
                      {item.priority} Priority
                    </PriorityBadge>
                  </ActionItemHeader>
                  <ActionDescription>{item.description}</ActionDescription>
                </ActionItemCard>
              ))}
            </ActionItemsGrid>
          )}
        </TabContent>
      </Container>
    </Section>
  );
};

export default MeetingNotes;
